import { useEffect, useState } from 'react';
import { getAuthConfig } from '../api/authConfig';

/**
 * FIX 5.27 (§13.11-13, COM-05…07) — platform switches from `GET /auth/config`,
 * as the portal and mobile read them. While `whatsapp` is off (delivery is
 * deferred) the WhatsApp nav items and the dashboard cost card stay hidden.
 */
export interface PlatformFeatures {
  readonly whatsapp: boolean;
}

// Optional surfaces start hidden until the API says otherwise.
const DEFAULTS: PlatformFeatures = { whatsapp: false };

let cached: PlatformFeatures | null = null;
let inflight: Promise<PlatformFeatures> | null = null;
let generation = 0;

/** One fetch per session (shared by concurrent callers); a failure retries next time. */
export function loadPlatformFeatures(): Promise<PlatformFeatures> {
  if (cached) return Promise.resolve(cached);
  const gen = generation;
  inflight ??= getAuthConfig()
    .then((cfg) => {
      const features: PlatformFeatures = { whatsapp: cfg.features?.whatsapp === true };
      if (gen === generation) cached = features;
      return features;
    })
    .catch(() => {
      if (gen === generation) inflight = null;
      return DEFAULTS;
    });
  return inflight;
}

/** The switches; the all-off defaults until the API has answered. */
export function usePlatformFeatures(): PlatformFeatures {
  const [features, setFeatures] = useState<PlatformFeatures>(() => cached ?? DEFAULTS);
  useEffect(() => {
    let alive = true;
    void loadPlatformFeatures().then((f) => {
      if (alive) setFeatures(f);
    });
    return () => {
      alive = false;
    };
  }, []);
  return features;
}

/** Test-only: forget the cached switches so the next mount re-fetches. */
export function __resetPlatformFeaturesCache(): void {
  cached = null;
  inflight = null;
  generation += 1;
}
